"use client";

import { PATHS } from "@/constants/pathnames";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLoading } from "@/contexts/LoadingContext";
import { useRouter } from "@/i18n/navigation";

import {
  ferrySearchReducer,
  initialState,
} from "@/reducers/ferrySearchReducer";
import createFerrySearchSchema from "@/schemas/ferrySearchSchema";
import ferryServices from "@/services/ferryServices";
import { staticDataService } from "@/services/staticService";
import { formatDate } from "@/utils/dateFormatter";
import { getLocalizedPath } from "@/utils/pathUtils";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useReducer } from "react";

export const useFerrySearch = (initialData = {}, t) => {
  // Initial data ile state'i merge et
  const mergedInitialState = {
    ...initialState,
    ...initialData,
  };

  const [state, dispatch] = useReducer(ferrySearchReducer, mergedInitialState);
  const locale = useLocale();
  const { currency } = useCurrency();
  const router = useRouter();

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };
  const setDepartureDate = (departureDate) => {
    dispatch({ type: "SET_DEPARTURE_DATE", payload: departureDate });
  };
  const setReturnDate = (returnDate) => {
    dispatch({ type: "SET_RETURN_DATE", payload: returnDate });
  };

  const setDepartureCity = useCallback((departureCity) => {
    dispatch({ type: "SET_DEPARTURE_CITY", payload: departureCity });
    dispatch({ type: "SET_RETURN_CITY", payload: null });
    dispatch({ type: "SET_IS_COUPON_SALE", payload: false });
  }, []);

  const setReturnCity = useCallback((returnCity) => {
    dispatch({ type: "SET_RETURN_CITY", payload: returnCity });
  }, []);

  const toggleDepartureAndReturn = () => {
    dispatch({ type: "TOGGLE_DEPARTURE_AND_RETURN" });
  };

  const setLatestFerrySearches = (latestFerrySearches) => {
    dispatch({
      type: "SET_LATEST_FERRY_SEARCHES",
      payload: latestFerrySearches,
    });
  };

  const setPopularCities = (popularCities) => {
    dispatch({ type: "SET_POPULAR_CITIES", payload: popularCities });
  };

  const setPublicHolidays = (publicHolidays) => {
    dispatch({ type: "SET_PUBLIC_HOLIDAYS", payload: publicHolidays });
  };

  const setActiveTab = useCallback(
    (data) => {
      // Eğer aynı tab'a set ediliyorsa, gereksiz re-render'ı önle
      if (state.activeTab === data) {
        return;
      }
      dispatch({ type: "SET_ACTIVE_TAB", payload: data });
      // Sadece oneway'e geçerken ve returnDate null değilse set et
      if (data === "oneway" && state.returnDate !== null) {
        dispatch({ type: "SET_RETURN_DATE", payload: null });
      } else if (data === "roundtrip") {
        const base =
          state.departureDate instanceof Date
            ? state.departureDate
            : state.departureDate
              ? new Date(state.departureDate)
              : new Date();

        const nextReturn = new Date(base);
        nextReturn.setDate(base.getDate() + 2);

        dispatch({
          type: "SET_RETURN_DATE",
          payload: nextReturn,
        });
      }
    },
    [state.activeTab, state.returnDate, state.departureDate],
  );

  const setIsWithVehicle = (isWithVehicle) => {
    dispatch({ type: "SET_IS_WITH_VEHICLE", payload: isWithVehicle });
  };

  const setVehicleType = (vehicleType) => {
    dispatch({ type: "SET_VEHICLE_TYPE", payload: vehicleType });
  };

  const setChildAges = (childAges) => {
    dispatch({ type: "SET_CHILD_AGES", payload: childAges });
  };

  const setTicketCount = useCallback((ticketCount) => {
    // GenericDropdown obje gönderiyor: { value: "1", label: "1" }
    const payload = ticketCount?.value ? Number(ticketCount.value) : 1;
    dispatch({ type: "SET_TICKET_COUNT", payload });
  }, []);

  const setFerryVehicles = (ferryVehicles) => {
    dispatch({ type: "SET_FERRY_VEHICLES", payload: ferryVehicles });
  };

  const setTempPassengers = useCallback((tempPassengers) => {
    dispatch({ type: "SET_TEMP_PASSENGERS", payload: tempPassengers });
  }, []);

  const setPassengers = useCallback((passengers) => {
    dispatch({ type: "SET_PASSENGERS", payload: passengers });
  }, []);

  // Ferry için sadece gerekli fonksiyonları kullan
  const getPopularCities = async () => {
    try {
      const popularCitiesResponse =
        await ferryServices.search.ferryPopularOrigins({
          languageCode: locale.toUpperCase(),
          headers: {
            "Accept-Language": locale,
          },
        });
      const popularCities = popularCitiesResponse.slice(0, 10) || null;
      setPopularCities(popularCities);
    } catch (error) {
      console.error(
        "getPopularCities: Popular cities yüklenirken hata oluştu:",
        error,
      );
    }
  };

  // Lazy-load flags and loaders
  const ensurePopularLoaded = useCallback(async () => {
    try {
      // Load if missing only
      if (!state.popularCities) {
        await getPopularCities();
      }
    } catch (_) {
      // no-op, errors already logged inside fetchers
    }
  }, [state.popularCities]);

  // Search for ferry terminals using flight service (temporary)
  const searchForFerryOrigins = useCallback(
    async (query) => {
      const response = await ferryServices.search.ferryOriginSearch({
        term: query ?? "",
        languageCode: locale.toUpperCase(),
        headers: {
          "Accept-Language": locale,
        },
      });
      return response;
    },
    [locale],
  );

  const searchForFerryDestinations = useCallback(
    async (query) => {
      const response = await ferryServices.search.ferryDestinationSearch({
        term: query ?? "",
        languageCode: locale.toUpperCase(),
        originSlug: state?.departureCity?.id || "",
        headers: {
          "Accept-Language": locale,
        },
      });
      return response;
    },
    [locale, state?.departureCity],
  );

  const searchForFerryVehicles = useCallback(async () => {
    const response = await ferryServices.search.getFerryVehicles({
      originSlug: state?.departureCity?.id || "",
      destinationSlug: state?.returnCity?.id || "",
      languageCode: locale.toUpperCase(),
      headers: {
        "Accept-Language": locale,
      },
    });
    setFerryVehicles(response);
    if (response && response.length === 0) {
      setIsWithVehicle(false);
      setVehicleType(null);
    }
    // return response;
  }, [state.departureCity, state.returnCity, locale]);

  useEffect(() => {
    if (!state?.departureCity?.id || !state?.returnCity?.id) {
      // Sadece değer değiştiyse dispatch et
      if (state.isCouponSale !== false) {
        dispatch({ type: "SET_IS_COUPON_SALE", payload: false });
      }
      return;
    }
    if (state?.departureCity?.id && state?.returnCity?.id) {
      searchForFerryVehicles();
    }
    const shouldBeCouponSale =
      state?.returnCity?.RouteType === "CouponSale" ||
      state?.departureCity?.RouteType === "CouponSale" ||
      initialData.isCouponSale;
    // Sadece değer değiştiyse dispatch et
    if (state.isCouponSale !== shouldBeCouponSale) {
      dispatch({ type: "SET_IS_COUPON_SALE", payload: shouldBeCouponSale });
    }
  }, [state.departureCity, state.returnCity, searchForFerryVehicles]);

  // City selection handlers - ferry için basitleştirilmiş
  const onSelectFromCity = useCallback(
    (city) => {
      setDepartureCity(city);
    },
    [setDepartureCity],
  );

  const onSelectToCity = useCallback(
    (city) => {
      setReturnCity(city);
    },
    [setReturnCity],
  );

  // Utility functions
  const formatDateForUrl = useCallback((date) => {
    return formatDate(date, { format: "YYYY-MM-DD" });
  }, []);

  // Ferry için URL oluşturma
  // TODO burak: servisler gelince feribota gore formatı duzenlenmeli aynı şekilde generateSearchUrl burdaki bus ile alakalı url oluşturucuda düzenlenmeli hatta bu iki fonk ortak hale gelmeli.
  const generateFerrySearchUrl = useCallback(() => {
    // Flight search ile aynı formatta tek parametre olarak birleştir
    const stationInfo = `${state.departureCity?.id || "from"}-${state.returnCity?.id || "to"}`;
    const depDate = formatDateForUrl(state.departureDate);
    const retDate = state.returnDate
      ? formatDateForUrl(state.returnDate)
      : null;

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Ferry yolcu bilgilerini ekle (sadece 0'dan büyük olanları)
    // Ferry için: adult, young, child
    if (state.passengers.adult > 0)
      queryParams.set("adult", state.passengers.adult.toString());
    // if (state.passengers.young > 0)
    //   queryParams.set("young", state.passengers.young.toString());
    if (state.passengers.child > 0)
      queryParams.set("child", state.passengers.child.toString());
    if (state.passengers.childAges.length > 0)
      queryParams.set("childAges", state.passengers.childAges.join(","));
    if (state.passengers.isWithVehicle)
      queryParams.set("vehicle", state.passengers.vehicleType.value.toString());

    // URL'de campaignId varsa koru (search sayfasından gelince kaybolmasın)
    try {
      if (typeof window !== "undefined") {
        const current = new URLSearchParams(window.location.search || "");
        const rawCampaignId = current.get("campaignId");
        if (rawCampaignId) {
          const normalized = String(rawCampaignId)
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
          if (normalized.length > 0) {
            queryParams.set("campaignId", normalized.join(","));
          }
        }
      }
    } catch {
      // no-op: campaignId korunamasa da arama çalışmalı
    }

    let baseUrl = "";
    if (state.activeTab === "oneway") {
      baseUrl = getLocalizedPath(PATHS.FERRY_TICKET_ONE_WAY.PATH, locale)
        .replace("[stationInfo]", `${stationInfo}`)
        .replace("[departureDate]", depDate);
    } else {
      baseUrl = getLocalizedPath(PATHS.FERRY_TICKET_ROUND_TRIP.PATH, locale)
        .replace("[stationInfo]", `${stationInfo}`)
        .replace("[departureDate]", depDate)
        .replace("[returnDate]", retDate);
    }

    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [
    state.departureCity,
    state.returnCity,
    state.departureDate,
    state.returnDate,
    state.activeTab,
    formatDateForUrl,
    state.passengers,
  ]);

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      departureCity: state.departureCity,
      returnCity: state.returnCity,
    };
    try {
      const schema = createFerrySearchSchema(t);
      schema.parse(formData);
      return {};
    } catch (error) {
      const errors = {};
      error.errors.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      return errors;
    }
  }, [state.departureCity, state.returnCity, t]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });
      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  // const getFerryDefaultOrigin = async () => {
  //   try {
  //     const flightDefaultOriginResponse =
  //       await flightService.getFlightDefaultOrigin({
  //         LanguageCode: locale.toUpperCase(),
  //       });
  //     setDepartureCity(
  //       flightDefaultOriginResponse?.OriginEntity?.AutoCompleteResult,
  //     );
  //   } catch (error) {
  //     console.error(
  //       "getFerryDefaultOrigin: Ferry default origin yüklenirken hata oluştu:",
  //       error,
  //     );
  //   }
  // };

  useEffect(() => {
    const STORAGE_KEY = initialData.storageKey || "latestFerrySearches";
    const latestFerrySearches = localStorage.getItem(STORAGE_KEY);
    if (latestFerrySearches) {
      const parsedSearches = JSON.parse(latestFerrySearches);
      // Eğer 3'ten fazla arama varsa, son 3 tanesini al
      const limitedSearches =
        parsedSearches.length > 3 ? parsedSearches.slice(0, 3) : parsedSearches;

      setLatestFerrySearches(limitedSearches);
    }

    getPublicHolidays();

    // Eğer initial state'de departureCity yoksa default origin'i getir
    if (!initialData.initialDepartureCityCode && locale) {
      // getFerryDefaultOrigin();
    }
  }, [locale]);

  // Initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadInitialCityData = async () => {
      try {
        // // Departure city kodunu kontrol et
        // if (initialData.initialDepartureCityCode && !state.departureCity) {
        //   setDepartureCity(initialData.initialDepartureCityCode);
        // }
        // // Return city kodunu kontrol et
        // if (initialData.initialReturnCityCode && !state.returnCity) {
        //   setReturnCity(initialData.initialReturnCityCode);
        // }
      } catch (error) {
        console.error(
          "loadInitialCityData: Initial şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      }
    };

    // Sadece locale yüklendiyse çalıştır
    if (
      locale &&
      (initialData.initialDepartureCityCode ||
        initialData.initialReturnCityCode)
    ) {
      loadInitialCityData();
    }
  }, [
    locale,
    initialData.initialDepartureCityCode,
    initialData.initialReturnCityCode,
  ]);

  const parseCampaignIdFromUrl = (searchString) => {
    try {
      let search = searchString;
      if (!search) {
        if (typeof window === "undefined") return [];
        search = window.location.search || "";
      }
      const params = new URLSearchParams(search);
      // Check both campaignId (correct)
      const raw = params.get("campaignId");
      if (!raw) return [];
      return String(raw)
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    } catch {
      return [];
    }
  };

  const handleSearchCouponSale = useCallback(async () => {
    // show();
    // State'ten güncel değerleri al
    const currentTicketCount = state.passengers.ticketCount || 1;
    const currentDepartureCity = state.departureCity;
    const currentReturnCity = state.returnCity;
    const currentVehicleType = state.passengers.vehicleType;

    const payload = {
      searchForm: {
        SelectedCampaigns: parseCampaignIdFromUrl(),
        SelectedDepartureTripId: "",
        SelectedReturnTripId: "",
        paxes: [{ type: "ADT", age: 30 }],
        legs: [
          {
            origin: currentDepartureCity?.id,
            destination: currentReturnCity?.id,
            departureDay: (() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return tomorrow;
            })(),
          },
        ],
        vehicles: [{ Id: currentVehicleType?.value, Count: 1 }],
        TicketCount: currentTicketCount,
        SelectedSorting: {},
        SelectedFilters: {
          DepartureTimeRange: {},
          ArrivalTimeRange: {},
          ReturnDepartureTimeRange: {},
          ReturnArrivalTimeRange: {},
          Companies: [],
        },
      },
    };
    const allocateCouponSaleResponse =
      await ferryServices.search.allocateCouponSale(payload, {
        "Accept-Language": locale,
      });
    if (allocateCouponSaleResponse && allocateCouponSaleResponse.BasketId) {
      router.push(`/ferry/reservation/${allocateCouponSaleResponse.BasketId}`);
    }
  }, [state, locale, router, parseCampaignIdFromUrl]);

  return {
    // Ferry-specific state mapping
    fromCity: state.departureCity,
    toCity: state.returnCity,
    departureDate: state.departureDate,
    returnDate: state.returnDate,
    passengers: state.passengers,
    latestFerrySearches: state.latestFerrySearches,
    popularDestinations: state.popularCities,
    publicHolidays: state.publicHolidays,
    activeTab: state.activeTab,
    isWithVehicle: state.passengers.isWithVehicle,
    vehicleType: state.passengers.vehicleType,
    childAges: state.passengers.childAges,
    isCouponSale: state.isCouponSale,
    ticketCount: state.passengers.ticketCount,
    ferryVehicles: state.ferryVehicles,
    tempPassengers: state.tempPassengers,
    // Actions
    setDepartureDate,
    setReturnDate,
    setDepartureCity,
    setReturnCity,
    toggleFromAndTo: toggleDepartureAndReturn,
    setActiveTab,
    setIsWithVehicle,
    setVehicleType,
    setChildAges,
    setTicketCount,
    setTempPassengers,
    setPassengers,
    // Service functions
    searchForFerryOrigins,
    searchForFerryDestinations,
    onSelectFromCity,
    onSelectToCity,
    ensurePopularLoaded,

    // Utility functions
    validateForm,
    generateFerrySearchUrl,
    refreshPublicHolidays: getPublicHolidays,
    handleSearchCouponSale,

    // State for compatibility
    state,
  };
};
